import { useState } from "react"
import { MenuItem, ListItemText } from "@mui/material"
import { useTranslation } from "react-i18next"

import Checkbox from "@/components/form/checkbox/Checkbox"
import Chip from "@/components/chip/Chip"
import Selector from "@/components/form/selector/Selector"
import { CheckboxColor } from "@/types/components/checkbox"

export type MultiSelectFilterOption<V extends string | number> = {
  value: V
  label: string
  secondary?: string
}

type MultiSelectFilterProps<V extends string | number> = {
  /** 트리거에 표시할 라벨 (예: "Activity Type") — 원래 Activity 필터 트리거 스타일 유지 */
  label: string
  options: MultiSelectFilterOption<V>[]
  selected: V[]
  onChange: (next: V[]) => void
  /** 끝에 추가되는 "None" 옵션 (예: 시즌위크 미지정) */
  noneOption?: MultiSelectFilterOption<V>
  emptyText?: string
}

/**
 * Activity 목록 필터 드롭다운.
 * - 트리거: 원래 Activity 필터와 동일 (라벨 + 요약 칩)
 * - 메뉴(리스트): 리포트 액티비티 필터와 동일 — 상단 "All" 토글 + 체크박스 + 선택적 "None"
 */
export default function MultiSelectFilter<V extends string | number>({
  label,
  options,
  selected,
  onChange,
  noneOption,
  emptyText,
}: MultiSelectFilterProps<V>) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const allOptions = noneOption ? [...options, noneOption] : options
  const allValues = allOptions.map((o) => o.value)
  const allSelected = allValues.length > 0 && selected.length === allValues.length

  const toggleAll = () => {
    onChange(allSelected ? [] : allValues)
  }

  const toggleOne = (value: V) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])
  }

  // 원래 Activity 트리거 요약 칩: 전체/없음 → "All", 1개 → 라벨, 여러개 → "첫라벨 +N"
  const selectedLabels = allOptions.filter((o) => selected.includes(o.value)).map((o) => o.label)
  const summaryChip = () => {
    if (!selectedLabels.length || allSelected) {
      return <Chip type="default-small" label={t("all")} />
    }
    if (selectedLabels.length === 1) {
      return <Chip type="default-small" label={selectedLabels[0]} />
    }

    return (
      <Chip type="default-small" label={`${selectedLabels[0]} +${selectedLabels.length - 1}`} />
    )
  }

  const renderOption = (opt: MultiSelectFilterOption<V>) => (
    <MenuItem
      key={String(opt.value)}
      value={String(opt.value)}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        toggleOne(opt.value)
      }}>
      <Checkbox checked={selected.includes(opt.value)} size="medium" />
      <ListItemText primary={opt.label} secondary={opt.secondary} />
    </MenuItem>
  )

  return (
    <Selector
      type="select"
      multiple
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selected.map(String) as never}
      onChange={() => {}}
      renderValue={() => (
        <span className="report-filter-selector-value">
          {label}
          {summaryChip()}
        </span>
      )}
      placeholder={t("select")}>
      <MenuItem
        key="__all"
        onClick={toggleAll}
        sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
        <Checkbox
          checked={allSelected}
          indeterminate={selected.length > 0 && !allSelected}
          size="medium"
          color={CheckboxColor.PLATINUM}
        />
        <ListItemText primary={t("all")} />
      </MenuItem>
      {options.length === 0 && !noneOption && (
        <MenuItem disabled>
          <ListItemText primary={emptyText ?? t("messages.noDataAvailable")} />
        </MenuItem>
      )}
      {options.map(renderOption)}
      {noneOption && renderOption(noneOption)}
    </Selector>
  )
}
